import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@/components/Button';
import { Col } from '@/components/Col';
import { Modal } from '@/components/Modal';
import { SectionHeader } from '@/components/SectionHeader';
import { Typography } from '@/components/Typography';
import type { ContentfulPlaceholderServiceFragment } from '@/graphql/contentful/api';
import { useService } from '@/hooks/useService';
import { genericErrorHandler } from '@/lib/forms/helpers';
import { getWarranty, getWatchDetailsSecurely } from '@/lib/service/api';
import type { ApiErrorWithType } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { serviceMessages } from '@/messages/service';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import type { ContinueParams } from '../IdentifyWatch/IdentifyWatch';
import { IdentifyWatch } from '../IdentifyWatch/IdentifyWatch';

import OnlineServiceIcon from '@/assets/icons/online-service.svg';

import * as S from './IdentifyWatchWithResult.styles';
export const IdentifyWatchWithResult = (
  props: ContentfulPlaceholderServiceFragment,
) => {
  const { ecsConfig } = useConfigProvider();
  const { navToServiceRequest } = useService();
  const { formatMessage } = useIntl();
  const {
    query: { languageCode },
  } = useRegionProvider();
  const [errors, setErrors] = useState<ApiErrorWithType[]>([]);
  const [serialNumber, setSerialNumber] = useState('');
  const [isProcessing, setIsProcessing] = useState<boolean>();
  const [isOpenWatchNotFoundModal, setIsOpenWatchNotFoundModal] =
    useState<boolean>();
  const errorHandler = genericErrorHandler({
    formatMessage,
    apiErrorsHandler: (errors, errorType) =>
      setErrors(
        errors.map((error) => ({
          type: errorType,
          ...error,
        })),
      ),
    errorType: 'service',
  });
  const handleSubmit = async (params: ContinueParams) => {
    try {
      setIsProcessing(true);
      setSerialNumber(params.sn);
      const warrantyData = await getWarranty(params.sn);
      if (
        !warrantyData?.code ||
        warrantyData.code == 801 ||
        !warrantyData.data?.sku
      ) {
        setIsProcessing(false);
        setIsOpenWatchNotFoundModal(true);
        return;
      }
      const res = await getWatchDetailsSecurely(
        warrantyData.data.sku,
        languageCode,
      );
      if (!res?.data.productVariant) {
        setIsProcessing(false);
        setIsOpenWatchNotFoundModal(true);
        return;
      }

      await navToServiceRequest(params.sn);
      setIsProcessing(false);
    } catch (error) {
      errorHandler([{ code: 'error' }]);
      setIsProcessing(false);
    }
  };
  if (!ecsConfig?.ecsAvailable) return null;
  return (
    <>
      <IdentifyWatch
        {...props}
        data-test-id='IdentifyWatchWithResult-IdentifyWatch'
        errors={errors}
        icon={OnlineServiceIcon}
        isProcessing={isProcessing}
        onSkip={() => navToServiceRequest()}
        onSubmit={handleSubmit}
        submitText={formatMessage(commonMessages.search)}
        variant='lightGrey'
      ></IdentifyWatch>
      <Modal
        isOpen={isOpenWatchNotFoundModal}
        onClose={() => setIsOpenWatchNotFoundModal(false)}
      >
        <Modal.Header
          divider
          onClose={() => setIsOpenWatchNotFoundModal(false)}
        >
          <SectionHeader
            sizeDesktop={40}
            sizeMobile={34}
            text={formatMessage(serviceMessages.identifyYourWatch)}
            textAlign='center'
          />
        </Modal.Header>
        <Modal.Body>
          <S.Empty
            alignItems='center'
            flexDirection='column'
            gridGap={1}
            maxWidth='537px'
            py={1}
          >
            <Typography scale='h2' textAlign='center' textTransform='uppercase'>
              {formatMessage(serviceMessages.noWatchFind)}
            </Typography>
            <Typography textAlign='center'>
              {formatMessage(serviceMessages.identifyWatchTip)}
            </Typography>
            <S.Wrapper gapDensity='none' mt={2}>
              <Col sm={6} xs={12}>
                <Button
                  isFullWidth
                  onClick={() => setIsOpenWatchNotFoundModal(false)}
                  variant='outlined'
                >
                  {formatMessage(serviceMessages.back)}
                </Button>
              </Col>
              <Col sm={6} xs={12}>
                <Button
                  data-testid='buttonOrderService'
                  isFullWidth
                  onClick={() => navToServiceRequest(serialNumber)}
                >
                  {formatMessage(serviceMessages.orderAService)}
                </Button>
              </Col>
            </S.Wrapper>
          </S.Empty>
        </Modal.Body>
      </Modal>
    </>
  );
};

IdentifyWatchWithResult.displayName = 'IdentifyWatchWithResult';
